import { Expandable, Modal } from '@warp-ds/react';
import { SmallFhhMap } from '../smallFhhMap/SmallFhhMap';
import { BrokerFilter } from './components/brokerFilter/BrokerFilter';
import { CheckboxFilter } from './components/checkboxFilter/CheckboxFilter';
import { PriceFilter } from './components/priceFilter/PriceFilter';
import { TransactionalFilter } from './components/transactionalFilter/TransactionalFilter';
import { houseRulesList, propertyTypesList } from './constants';
import type { FilterProps } from './types';

interface FilterPropsWithData extends FilterProps {
    data: {
        facilities?: string[];
        pois_nearby?: string[];
    };
    showMap?: boolean;
    contentOnly?: boolean;
    showMobileModal?: boolean;
    onDismiss?: () => void;
}

const expandableProps = {
    box: true,
    buttonClass: 's-bg rounded-0',
    contentClass: 's-bg rounded-0',
    headingLevel: 3 as const,
    animated: true,
};

export const Filter = ({
    data,
    filters,
    onFilterChanged,
    showMap = true,
    contentOnly = false,
    showMobileModal = false,
    onDismiss,
}: FilterPropsWithData) => {
    const filterContent = (
        <>
            <h2 className="sr-only">Filtre</h2>
            {showMap && <SmallFhhMap filters={filters} />}
            <form className="divide-y-1">
                <Expandable {...expandableProps} expanded title="Utleier">
                    <BrokerFilter filters={filters} onFilterChanged={onFilterChanged} />
                </Expandable>
                {(!Object.hasOwn(filters, 'country') || filters.country === 'Norge') && (
                    <Expandable {...expandableProps} expanded title="Bestilling og betaling">
                        <TransactionalFilter filters={filters} onFilterChanged={onFilterChanged} />
                    </Expandable>
                )}
                <Expandable {...expandableProps} expanded title="Pris" data-testid="FilterGroup-pris">
                    <PriceFilter filters={filters} onFilterChanged={onFilterChanged} />
                </Expandable>
                <Expandable {...expandableProps} title="Boligtype">
                    <CheckboxFilter
                        onFilterChanged={onFilterChanged}
                        filterData={propertyTypesList}
                        filterValues={filters.property_types}
                        filterType="property_types"
                        legend="Boligtype"
                    />
                </Expandable>
                <Expandable {...expandableProps} title="Husregler og tilgjengelighet">
                    <CheckboxFilter
                        onFilterChanged={onFilterChanged}
                        filterData={houseRulesList}
                        filterValues={filters.house_rules}
                        filterType="house_rules"
                        legend="Husregler og tilgjengelighet"
                    />
                </Expandable>
                <Expandable {...expandableProps} title="Fasiliteter">
                    <CheckboxFilter
                        onFilterChanged={onFilterChanged}
                        filterData={data.facilities}
                        filterValues={filters.facilities}
                        filterType="facilities"
                        legend="Fasiliteter"
                    />
                </Expandable>
                <Expandable {...expandableProps} title="I nærheten av">
                    <CheckboxFilter
                        onFilterChanged={onFilterChanged}
                        filterData={data.pois_nearby}
                        filterValues={filters.pois_nearby}
                        filterType="pois_nearby"
                        legend="I nærheten av"
                    />
                </Expandable>
            </form>
        </>
    );

    if (contentOnly) {
        return filterContent;
    }

    return (
        <>
            <section className="hidden md:block md:col-span-1" data-testid="filters">
                <aside>{filterContent}</aside>
            </section>
            {showMobileModal && onDismiss && (
                <Modal title="Endre filter" open={showMobileModal} right onDismiss={onDismiss}>
                    {filterContent}
                </Modal>
            )}
        </>
    );
};
